import React, { useState, useEffect, useCallback } from 'react';
import {
  Database,
  Download,
  Upload,
  Trash2,
  RefreshCw,
  Clock,
  Calendar,
  Check,
  X,
  AlertTriangle,
  Loader2,
  FileArchive,
  HardDrive,
  Shield,
  ChevronRight,
  Info,
  Eye,
  Lock,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Settings,
  Plus
} from 'lucide-react';
import api from '../../services/api';
import { formatIST, formatTimeAgoIST } from '../../utils/timezone';

/**
 * BackupsView Component
 * Enterprise-grade backup management UI for Database Explorer
 * All timestamps displayed in Indian Standard Time (IST - UTC+5:30)
 */

// Status badge colors
const STATUS_CONFIG = {
  COMPLETED: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2 },
  IN_PROGRESS: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: Loader2 },
  FAILED: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: XCircle },
  PENDING: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: Clock }
};

const TYPE_CONFIG = {
  SCHEDULED: { bg: 'bg-purple-50', text: 'text-purple-700', label: 'Scheduled' },
  MANUAL: { bg: 'bg-indigo-50', text: 'text-indigo-700', label: 'Manual' }
};

// Restore Confirmation Modal
const RestoreConfirmModal = ({ backup, onClose, onConfirm, loading }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!password.trim()) {
      setError('Please enter your admin password');
      return;
    }
    
    try {
      await onConfirm(password);
    } catch (err) {
      setError(err.message || 'Restore failed');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-red-50 rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-red-800">Confirm Database Restore</h3>
              <p className="text-sm text-red-600">This action cannot be undone</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Warning Message */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">Warning: Data Loss Risk</p>
                <p>Restoring from this backup will:</p>
                <ul className="list-disc ml-5 mt-1 space-y-1">
                  <li>Replace ALL current data with backup data</li>
                  <li>Permanently delete any changes made after the backup</li>
                  <li>Affect all collections ({backup.collections?.length || 0} collections)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Backup Details */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Backup ID:</span>
              <code className="text-xs bg-slate-200 px-2 py-0.5 rounded">{backup.backup_id}</code>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Created:</span>
              <span className="font-medium">{formatIST(backup.created_at)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Documents:</span>
              <span className="font-medium">{backup.total_documents?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Size:</span>
              <span className="font-medium">{backup.file_size_human}</span>
            </div>
          </div>

          {/* Password Input */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              <Lock className="w-4 h-4 inline mr-1" />
              Enter your admin password to confirm
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              autoFocus
              disabled={loading}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center gap-2">
              <XCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !password.trim()}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Restoring...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Confirm Restore
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Backup Modal
const CreateBackupModal = ({ onClose, onConfirm, loading }) => {
  const [notes, setNotes] = useState('');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Download className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Create Manual Backup</h3>
              <p className="text-sm text-slate-500">Create a snapshot of all database collections</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Before major update, End of day backup..."
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none"
              maxLength={500}
            />
            <p className="text-xs text-slate-400 mt-1">{notes.length}/500 characters</p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
            <Info className="w-4 h-4 inline mr-1" />
            This will backup all collections including parts, manufacturers, taxonomy, audit logs, and system data.
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(notes)}
              disabled={loading}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Create Backup
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Restore Procedure Panel
const RestoreProcedurePanel = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-slate-700">Restore Procedure Guide</span>
        </div>
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100">
          <div className="mt-4 space-y-4 text-sm">
            {/* Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              <strong>Warning:</strong> Restoring will REPLACE all current data with backup data.
            </div>

            {/* Prerequisites */}
            <div>
              <h4 className="font-semibold text-slate-700 mb-2">Prerequisites</h4>
              <ul className="space-y-1 text-slate-600">
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-green-600" />
                  You must have Admin role
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-green-600" />
                  Know your admin password
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-green-600" />
                  Notify users of potential interruption
                </li>
              </ul>
            </div>

            {/* Steps */}
            <div>
              <h4 className="font-semibold text-slate-700 mb-2">Steps to Restore</h4>
              <ol className="space-y-2 text-slate-600">
                <li className="flex gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-medium">1</span>
                  <span>Locate the backup you want to restore</span>
                </li>
                <li className="flex gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-medium">2</span>
                  <span>Verify status is "COMPLETED" and check date/document count</span>
                </li>
                <li className="flex gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-medium">3</span>
                  <span>Click the "Restore" button on the backup row</span>
                </li>
                <li className="flex gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-medium">4</span>
                  <span>Enter your admin password to confirm</span>
                </li>
                <li className="flex gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-medium">5</span>
                  <span>Wait for completion (typically 30-60 seconds)</span>
                </li>
                <li className="flex gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-medium">6</span>
                  <span>Verify data in the Data tab after restore</span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main BackupsView Component
const BackupsView = () => {
  const [backups, setBackups] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);

  const loadBackups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [backupsRes, configRes] = await Promise.all([
        api.get('/api/v1/backups'),
        api.get('/api/v1/backups/config')
      ]);
      setBackups(backupsRes.data.backups || []);
      setConfig(configRes.data);
    } catch (err) {
      console.error('Failed to load backups:', err);
      setError(err.response?.data?.detail || 'Failed to load backups. Make sure you have admin access.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBackups();
  }, [loadBackups]);

  const handleCreateBackup = async (notes) => {
    try {
      setActionLoading(true);
      await api.post('/api/v1/backups', { notes });
      setShowCreateModal(false);
      await loadBackups();
    } catch (err) {
      console.error('Failed to create backup:', err);
      throw new Error(err.response?.data?.detail || 'Failed to create backup');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestore = async (password) => {
    try {
      setActionLoading(true);
      const response = await api.post(`/api/v1/backups/${selectedBackup.backup_id}/restore`, { password });
      setRestoreResult(response.data);
      setShowRestoreModal(false);
      setSelectedBackup(null);
      await loadBackups();
    } catch (err) {
      console.error('Restore failed:', err);
      throw new Error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('Are you sure you want to delete this backup? This cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/api/v1/backups/${backupId}`);
      await loadBackups();
    } catch (err) {
      console.error('Failed to delete backup:', err);
      alert(err.response?.data?.detail || 'Failed to delete backup');
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="text-sm text-slate-500">Loading backups...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Access Denied</span>
          </div>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col gap-4 p-4 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-50 rounded-lg">
            <HardDrive className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Database Backups</h3>
            <p className="text-xs text-slate-500">
              {config?.retention_days || 90}-day retention • {config?.schedule || 'Daily at 2:00 AM IST'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadBackups}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Backup
          </button>
        </div>
      </div>

      {/* Restore Result Banner */}
      {restoreResult && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <div>
                <p className="font-medium text-emerald-800">Restore Completed Successfully</p>
                <p className="text-sm text-emerald-600">
                  {restoreResult.total_documents_restored?.toLocaleString()} documents restored from {restoreResult.collections_restored?.length} collections
                </p>
              </div>
            </div>
            <button onClick={() => setRestoreResult(null)} className="text-emerald-600 hover:text-emerald-800">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-3 border border-emerald-200">
          <div className="flex items-center gap-2 text-emerald-600 mb-1">
            <Database className="w-4 h-4" />
            <span className="text-xs font-medium">Total Backups</span>
          </div>
          <div className="text-2xl font-bold text-emerald-700">
            {config?.total_backups || 0}
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
          <div className="flex items-center gap-2 text-blue-600 mb-1">
            <Check className="w-4 h-4" />
            <span className="text-xs font-medium">Completed</span>
          </div>
          <div className="text-2xl font-bold text-blue-700">
            {config?.completed_backups || 0}
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200">
          <div className="flex items-center gap-2 text-purple-600 mb-1">
            <HardDrive className="w-4 h-4" />
            <span className="text-xs font-medium">Total Size</span>
          </div>
          <div className="text-xl font-bold text-purple-700">
            {config?.total_backup_size_human || '0 B'}
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-3 border border-amber-200">
          <div className="flex items-center gap-2 text-amber-600 mb-1">
            <Calendar className="w-4 h-4" />
            <span className="text-xs font-medium">Retention</span>
          </div>
          <div className="text-2xl font-bold text-amber-700">
            {config?.retention_days || 90}d
          </div>
        </div>
      </div>

      {/* Restore Procedure Guide */}
      <RestoreProcedurePanel />

      {/* Backups Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden flex-1">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <FileArchive className="w-4 h-4" />
            Backup History
            <span className="text-xs font-normal text-slate-400">
              ({backups.length} backup{backups.length !== 1 ? 's' : ''})
            </span>
          </h4>
        </div>

        {backups.length > 0 ? (
          <div className="overflow-auto max-h-[400px]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-slate-600">Created</th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-600">Type</th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-600">Size</th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-600">Documents</th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-600">Duration</th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-600">Expires</th>
                  <th className="px-4 py-2 text-right font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {backups.map((backup) => {
                  const statusConfig = STATUS_CONFIG[backup.status] || STATUS_CONFIG.PENDING;
                  const typeConfig = TYPE_CONFIG[backup.backup_type] || TYPE_CONFIG.MANUAL;
                  const StatusIcon = statusConfig.icon;

                  return (
                    <tr key={backup.backup_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium text-slate-700">{formatIST(backup.created_at, { includeSeconds: false })}</div>
                          <div className="text-xs text-slate-400">{formatTimeAgoIST(backup.created_at)}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeConfig.bg} ${typeConfig.text}`}>
                          {typeConfig.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border} border`}>
                          <StatusIcon className={`w-3 h-3 ${backup.status === 'IN_PROGRESS' ? 'animate-spin' : ''}`} />
                          {backup.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {backup.file_size_human || '—'}
                      </td>
                      <td className="px-4 py-3">
                        {backup.total_documents?.toLocaleString() || '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {backup.duration_seconds ? `${backup.duration_seconds}s` : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {formatIST(backup.expires_at, { includeTime: false })}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {backup.status === 'COMPLETED' && (
                            <button
                              onClick={() => { setSelectedBackup(backup); setShowRestoreModal(true); }}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Restore from this backup"
                            >
                              <Upload className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteBackup(backup.backup_id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete backup"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-12 text-center">
            <FileArchive className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No backups yet</p>
            <p className="text-xs text-slate-400 mt-1">
              Click "Create Backup" to create your first backup
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showRestoreModal && selectedBackup && (
        <RestoreConfirmModal
          backup={selectedBackup}
          onClose={() => { setShowRestoreModal(false); setSelectedBackup(null); }}
          onConfirm={handleRestore}
          loading={actionLoading}
        />
      )}

      {showCreateModal && (
        <CreateBackupModal
          onClose={() => setShowCreateModal(false)}
          onConfirm={handleCreateBackup}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default BackupsView;
